import React, { useState } from 'react';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import ErrorDetails from './ErrorDetails.jsx';

/* ═══════════════════════════════════════════════════════════
   Modelli LLM per attività — GET/PUT /admin/modelli (solo admin).
   Permette di passare, senza ridistribuire, fra Qwen locale (Ollama o
   vLLM) e API cloud, attività per attività: es. conversazione del
   Self-Assessment su API per la velocità, MultiFitting su Qwen locale.
   Conversazione ed estrazione del SA (dati personali) ammettono solo
   modelli locali (13/10): il cloud per quei dati è una scelta di privacy,
   non del pannello. L'interruttore «dati personali al cloud» non c'è più.
═══════════════════════════════════════════════════════════ */

const ETICHETTE = {
  conversation: 'Conversazione Self-Assessment',
  extraction: 'Estrazione risposte Self-Assessment',
  multifitting: 'MultiFitting (giudizio CV–annuncio)',
  scoring: 'Scoring',
  summary_commercial: 'Sintesi commerciali',
  text2sql: 'Interrogazioni in linguaggio naturale',
};

export default function ModelliLlmPanel() {
  const kmClient = useKmClient('km');
  const { data, status, error, refetch } = useKmQuery(() => kmClient.get('/admin/modelli'), []);
  const [salvataggio, setSalvataggio] = useState({ stato: 'fermo', errore: null });

  async function salva(modifiche) {
    setSalvataggio({ stato: 'in_corso', errore: null });
    try {
      await kmClient.put('/admin/modelli', modifiche);
      setSalvataggio({ stato: 'fatto', errore: null });
      refetch();
    } catch (e) {
      setSalvataggio({ stato: 'errore', errore: e });
    }
  }

  if (status === 'loading') return <div className="card"><div className="skeleton-line" style={{ width: '40%' }} /></div>;
  if (status === 'error') {
    return (
      <div className="state-error">
        Impostazioni dei modelli non disponibili (serve il ruolo admin).
        <ErrorDetails error={error} />
      </div>
    );
  }

  const opzioni = [...(data?.opzioni?.locali || []), ...(data?.opzioni?.cloud || [])];
  const locali = new Set(data?.opzioni?.locali || []);
  const soloLocali = new Set(data?.solo_locali || ['conversation', 'extraction']);

  return (
    <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Modelli LLM per attività</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(260px, 2fr)', gap: 'var(--space-2) var(--space-4)', alignItems: 'center' }}>
        {(data?.attivita || []).map((attivita) => {
          const corrente = data?.modelli?.[attivita] || '';
          const ammesse = soloLocali.has(attivita) ? [...locali] : opzioni;
          const scelte = ammesse.includes(corrente) ? ammesse : [corrente, ...ammesse];
          return (
            <React.Fragment key={attivita}>
              <label htmlFor={`modello-${attivita}`} style={{ fontSize: 'var(--font-size-sm)' }}>{ETICHETTE[attivita] || attivita}</label>
              <select
                id={`modello-${attivita}`}
                className="field-select"
                value={corrente}
                disabled={salvataggio.stato === 'in_corso'}
                onChange={(e) => salva({ modelli: { [attivita]: e.target.value } })}
              >
                {scelte.map((m) => (
                  <option key={m} value={m}>{m} {locali.has(m) || m.startsWith('ollama/') || m.startsWith('vllm/') ? '· locale' : '· cloud'}</option>
                ))}
              </select>
            </React.Fragment>
          );
        })}
      </div>

<p style={{ marginTop: 'var(--space-4)', marginBottom: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
        {data?.nota}
      </p>

      {salvataggio.stato === 'fatto' && <div style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>Salvato: vale entro 30 secondi.</div>}
      {salvataggio.stato === 'errore' && <div className="state-error"><ErrorDetails error={salvataggio.errore} /></div>}
    </div>
  );
}
